import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Eye, EyeOff, ArrowLeft, Loader2, AlertCircle} from 'lucide-react'
import Logo from '@/components/Logo'
import { lumi } from '@/lib/lumi'

// Credenciales hardcoded del administrador del sistema
const ADMIN_EMAIL = 'admin'
const ADMIN_PASSWORD = 'Ochodoce812'

type Mode = 'signin' | 'reset'

function Login() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [loading, setLoading] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState('')
  const [otpSent, setOtpSent] = useState(false)

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
      return () => clearTimeout(t)
    }
  }, [countdown])

  const sendOtp = async () => {
    if (!email) return setError('Por favor ingresa tu correo electrónico.')
    setOtpLoading(true)
    setError('')
    try {
      await lumi.auth.sendOtp({ email, type: 'resetPassword' })
      setCountdown(60)
      setOtpSent(true)
    } catch (e: any) {
      setError(e?.message || 'Error al enviar el código. Intenta de nuevo.')
    } finally {
      setOtpLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'signin') {
        // Verificar si es el admin del sistema
        if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
          // Autenticación local del sistema admin
          localStorage.setItem('beeznz_admin', 'true')
          navigate('/admin/dashboard', { replace: true })
          return
        }

        // Iniciar sesión. El SDK devuelve los datos del usuario directamente,
        // no dependemos de lumi.auth.user (que se actualiza de forma asíncrona).
        const signInResult: any = await lumi.auth.signInWithPassword({ email, password })

        // Obtener el userId de la respuesta del login o, como respaldo, del SDK.
        const userId =
          signInResult?.userId ||
          signInResult?.data?.userId ||
          lumi.auth.user?.userId

        // Determinar a dónde redirigir según el tipo de cuenta.
        let destination = '/colaborador/dashboard'
        if (userId) {
          try {
            const { list } = await lumi.entities.profiles.list({
              filter: { userId },
              limit: 1,
            })
            if (list.length > 0 && list[0]?.accountType === 'empresa') {
              destination = '/empresa/dashboard'
            }
          } catch {
            // Si falla la consulta de perfil, usamos el destino por defecto.
          }
        }

        // Navegación SPA (sin recarga completa del documento). Esto es clave
        // dentro del iframe del editor de Lumi, donde una recarga completa
        // pierde la sesión por el aislamiento de almacenamiento de terceros.
        navigate(destination, { replace: true })
        return
      } else {
        if (!otp) return setError('Por favor ingresa el código OTP.')
        await lumi.auth.resetPassword({ email, newPassword, otp })
        navigate('/colaborador/dashboard', { replace: true })
      }
    } catch (e: any) {
      const code = e?.data?.code
      const msgs: Record<number, string> = {
        2254: 'El registro público no está habilitado.',
        2259: 'Tu cuenta no está activa.',
        2261: 'El inicio de sesión por correo no está habilitado.',
        2262: 'El inicio de sesión con Google no está habilitado.',
      }
      setError(msgs[code] || e?.message || 'Credenciales incorrectas. Verifica tu correo y contraseña.')
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (m: Mode) => {
    setMode(m)
    setError('')
    setOtp('')
    setOtpSent(false)
    setCountdown(0)
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header */}
      <header className="flex h-16 items-center justify-between px-6 lg:px-10">
        <Link to="/" aria-label="Volver al inicio">
          <Logo />
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors">
          <ArrowLeft size={15} /> Volver
        </Link>
      </header>

      {/* Form */}
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mb-7 text-center">
              <h1 className="text-2xl font-bold text-slate-900">
                {mode === 'signin' ? 'Iniciar sesión' : 'Recuperar contraseña'}
              </h1>
              <p className="mt-1.5 text-sm text-slate-500">
                {mode === 'signin'
                  ? 'Bienvenido de regreso a Beeznz Work'
                  : 'Ingresa tu correo y te enviaremos un código'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {mode === 'signin' ? 'Correo electrónico o usuario' : 'Correo electrónico'}
                </label>
                <input
                  type={mode === 'reset' ? 'email' : 'text'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={mode === 'signin' ? 'correo@ejemplo.com' : 'correo@ejemplo.com'}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />
              </div>

              {/* OTP (reset) */}
              {mode === 'reset' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Código OTP</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Código de 6 dígitos"
                      className="flex-1 rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    />
                    <button
                      type="button"
                      onClick={sendOtp}
                      disabled={countdown > 0 || otpLoading}
                      className="whitespace-nowrap rounded-lg bg-slate-100 px-3.5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 disabled:opacity-50"
                    >
                      {otpLoading ? <Loader2 size={16} className="animate-spin" /> : countdown > 0 ? `${countdown}s` : 'Enviar código'}
                    </button>
                  </div>
                  {otpSent && <p className="mt-1.5 text-xs text-emerald-600">Código enviado. Revisa tu correo.</p>}
                </div>
              )}

              {/* Password */}
              {mode === 'signin' && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Contraseña</label>
                    <button type="button" onClick={() => switchMode('reset')} className="text-xs text-amber-600 hover:underline">
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 pr-10 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Nueva contraseña (reset) */}
              {mode === 'reset' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Nueva contraseña</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      required
                      className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 pr-10 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    />
                    <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3.5 py-3 text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading
                  ? 'Procesando...'
                  : mode === 'signin'
                  ? 'Iniciar sesión'
                  : 'Restablecer contraseña'}
              </button>
            </form>

            {mode === 'reset' && (
              <button
                type="button"
                onClick={() => switchMode('signin')}
                className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-800"
              >
                ← Volver a iniciar sesión
              </button>
            )}

            {mode === 'signin' && (
              <p className="mt-5 text-center text-sm text-slate-500">
                ¿No tienes cuenta?{' '}
                <Link to="/registro" className="font-semibold text-amber-600 hover:underline">
                  Regístrate aquí
                </Link>
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default Login
